import {ChangeEvent, FC, useCallback, useEffect, useRef, useState} from 'react';
import {Box, Typography, IconButton} from '@mui/material';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import './JobResultHeader.scss';
import ArrButton from '../../../../common/arr-button/ArrButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {createJobNameFieldSchema} from './jobResultHeaderSchema';
import {useUpdateAIReviewJobName} from '../../../../../hooks/ai/review/useReview';
import {getIssueCodingResultsDownload} from '../../../../../api/endpoints/aiReviewApi';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ReactComponent as DownloadIcon} from '../../../../../assets/images/downloadSVG.svg';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';

const JobResultHeader: FC = () => {
  const {state} = useLocation();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isErrorMessage, setIsErrorMessage] = useState<string>('');
  const [jobNameValue, setJobNameValue] = useState<string>(
    state?.job_name ?? ''
  );
  const [originalJobNameValue, setOriginalJobNameValue] = useState<string>(
    state?.job_name
  );
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType?.Success);

  const inputRef = useRef<HTMLInputElement>(null);
  const jobNameFieldSchema = createJobNameFieldSchema(t);

  const {mutate: updateJobName} = useUpdateAIReviewJobName();

  useEffect(() => {
    if (isEditing && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isEditing]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setJobNameValue(value);
    const result = jobNameFieldSchema.safeParse(value);
    const errorMessage = !result?.success ? result.error.issues[0].message : '';
    setIsErrorMessage(errorMessage);
  };

  const onHandleSave = () => {
    if (isErrorMessage) return;

    const trimmedValue = jobNameValue.trim();

    if (!trimmedValue) {
      setJobNameValue(originalJobNameValue);
      setIsEditing(false);
      return;
    }

    if (trimmedValue !== originalJobNameValue) {
      updateJobName(
        {
          jobId: state?.id,
          name: trimmedValue,
        },
        {
          onSuccess: () => {
            setOriginalJobNameValue(trimmedValue);
            setIsEditing(false);
          },
          onError: () => {
            setJobNameValue(originalJobNameValue);
          },
        }
      );
    } else {
      setIsEditing(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onHandleSave();
    }
  };

  const getSupportedText = (): string => {
    if (isErrorMessage) return isErrorMessage;

    return t('ai.review.jobResult.header.reachedChar50', {
      count: jobNameValue.length,
    });
  };

  const handleDownload = useCallback(async () => {
    try {
      const jobId = 2; // TODO: passing hardcoded jobID as 2, need to replace with dynamic jobId in future

      if (!jobId) {
        console.error('jobId is missing');
        return;
      }

      const response = await getIssueCodingResultsDownload(jobId);
      const {blob, filename} = response;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `issue-coding-results-${jobId}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed', error);
      setToastMessage(t('ai.review.header.downloadError'));
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  }, [t]);

  return (
    <Box className="jobresult-header">
      <Box className="jobresult-header-back-button">
        <ArrButton
          variant="text"
          size="small"
          aria-label={t('ai.review.jobResult.header.backToAIReview')}
          onClick={() => navigate('/AI/Review')}
          dataTestId="job-result-header-back-to-home-button"
        >
          <NavigateBeforeIcon fontSize="small" className="button-arrow" />
          {t('ai.review.jobResult.header.backToHome')}
        </ArrButton>
        <Box className="jobresult-header-jobname">
          {isEditing ? (
            <ArrTextField
              inputRef={inputRef}
              className="jobname-input"
              dataTestId="jobname-input-field"
              size="small"
              value={jobNameValue}
              slotProps={{
                htmlInput: {
                  maxLength: 50,
                },
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
              onBlur={onHandleSave}
              state={isErrorMessage ? 'error' : 'enabled'}
              onKeyDown={onKeyDown}
              supportText={getSupportedText()}
              showErrorIcon={!!isErrorMessage}
            />
          ) : (
            <>
              <Typography
                className="typo-text subhead1_size"
                tabIndex={0}
                aria-label={t('ai.review.jobResult.header.jobName')}
                data-testid={`job-result-header-job-name-value`}
              >
                {jobNameValue}
              </Typography>
              <IconButton
                className="icon-button"
                aria-label={t('ai.review.jobResult.header.editJobName', {
                  jobName: jobNameValue,
                })}
                data-testid="edit-jobname"
                onClick={() => setIsEditing(true)}
              >
                <EditOutlinedIcon className="svg-edit-icon" fontSize="medium" />
              </IconButton>
              <ArrTooltipWrapper
                trigger={
                  <InfoIcon
                    className="info-icon-label"
                    data-testid="toggle-info-tooltip"
                  />
                }
                title={t('ai.review.jobResult.header.jobNameTooltipText')}
              >
                {t('ai.review.jobResult.header.jobNameTooltipText')}
              </ArrTooltipWrapper>
            </>
          )}
        </Box>
      </Box>
      <Box>
        <ArrButton
          variant="primary"
          size="medium"
          aria-label={t('ai.review.jobResult.header.downloadResultDoc')}
          startIcon={<DownloadIcon />}
          dataTestId="job-result-header-download-button"
          className="job-result-header-download-button"
          onClick={handleDownload}
        >
          {t('ai.review.jobResult.header.download')}
        </ArrButton>
      </Box>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default JobResultHeader;
